import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { formatDate, LOCALE_MAP } from 'utils/locale-formatting'
import styled from 'styled-components'
import { Button, Dialog } from 'Components/ui'
import { PolicyCard } from '../../Shared'
import { SmallSpinner } from '../styles'
import { getProtectedPolicy } from 'services/transparency-service'

const Wrapper = styled.div``

const EmptyText = styled.p`
  font-size: 14px;
  color: var(--vo-slate);
`

const PoliciesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;
`

const ModalBody = styled.div``

const InfoSection = styled.div`
  margin-bottom: 24px;
`

const SectionLabel = styled.h4`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--vo-slate);
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`

const InfoItem = styled.div``

const InfoLabel = styled.div`
  font-size: 14px;
  color: var(--vo-slate);
`

const InfoValue = styled.div`
  font-size: 16px;
  color: var(--vo-ink);
`

const FullWidthInfo = styled.div`
  grid-column: 1 / -1;
`

const DetailPaper = styled.div`
  padding: 16px;
  background: var(--vo-mist);
  border: 1px solid var(--vo-cloud);
  border-radius: var(--radius-lg);
`

const DetailText = styled.div`
  font-size: 14px;
  white-space: pre-wrap;
  color: var(--vo-ink);
`

const ContentPaper = styled.div`
  padding: 16px;
  background: var(--vo-mist);
  border: 1px solid var(--vo-cloud);
  border-radius: var(--radius-lg);
  max-height: 300px;
  overflow: auto;
`

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`

const UnavailableText = styled.p`
  font-size: 14px;
  color: var(--vo-slate);
`

const PoliciesTab = ({ policies = [], companyKey, accessToken }) => {
  const { t } = useTranslation('transparency')
  const [selectedPolicy, setSelectedPolicy] = useState(null)

  if (!policies || policies.length === 0) {
    return <EmptyText>{t('protected.emptyPolicies')}</EmptyText>
  }

  return (
    <Wrapper>
      <PoliciesGrid>
        {policies.map((policy, index) => (
          <PolicyCard
            key={policy.id || index}
            name={policy.title}
            version={policy.version}
            status={policy.status}
            visibility={policy.visibility}
            lastUpdated={policy.lastUpdated}
            onClick={() => setSelectedPolicy(policy)}
          />
        ))}
      </PoliciesGrid>

      {selectedPolicy && (
        <PolicyDetailModal
          policy={selectedPolicy}
          companyKey={companyKey}
          accessToken={accessToken}
          onClose={() => setSelectedPolicy(null)}
        />
      )}
    </Wrapper>
  )
}

const PolicyDetailModal = ({ policy, companyKey, accessToken, onClose }) => {
  const { t, i18n } = useTranslation('transparency')
  const locale = LOCALE_MAP[i18n.language] ?? 'en-US'
  const { data: policyData, isLoading } = useQuery(
    ['policyDetail', policy.id, companyKey],
    () => getProtectedPolicy(policy.id, companyKey, accessToken),
    { enabled: !!policy.id }
  )

  const detail = policyData?.data

  return (
    <Dialog.custom
      open
      onClose={onClose}
      title={policy.title}
      actions={<Button onClick={onClose}>{t('protected.policyDialog.close')}</Button>}
    >
      <ModalBody>
        {isLoading ? (
          <LoadingRow>
            <SmallSpinner />
          </LoadingRow>
        ) : detail ? (
          <>
            <InfoSection>
              <SectionLabel>{t('protected.policyDialog.generalInfo')}</SectionLabel>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>{t('protected.policyDialog.version')}</InfoLabel>
                  <InfoValue>{detail.version || policy.version}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>{t('protected.policyDialog.status')}</InfoLabel>
                  <InfoValue>{detail.status || policy.status}</InfoValue>
                </InfoItem>
                {detail.lastUpdated && (
                  <FullWidthInfo>
                    <InfoLabel>{t('protected.policyDialog.lastUpdated')}</InfoLabel>
                    <InfoValue>{formatDate(detail.lastUpdated, locale)}</InfoValue>
                  </FullWidthInfo>
                )}
              </InfoGrid>
            </InfoSection>

            {detail.executiveSummary && (
              <InfoSection>
                <SectionLabel>{t('protected.policyDialog.executiveSummary')}</SectionLabel>
                <DetailPaper>
                  <DetailText>{detail.executiveSummary}</DetailText>
                </DetailPaper>
              </InfoSection>
            )}

            {detail.content && (
              <InfoSection>
                <SectionLabel>{t('protected.policyDialog.policyContent')}</SectionLabel>
                <ContentPaper>
                  <DetailText>{detail.content}</DetailText>
                </ContentPaper>
              </InfoSection>
            )}
          </>
        ) : (
          <UnavailableText>{t('protected.policyDetailUnavailable')}</UnavailableText>
        )}
      </ModalBody>
    </Dialog.custom>
  )
}

export { PoliciesTab }
